// src/pages/financials/PayrollTab.tsx
//
// Payroll — labor cost / overtime insights from the qbd-payroll-summary and
// qbd-payroll-employees recipes. Month-count pills (3/6/12) drive both
// queries. KPI tiles (gross pay, OT pay, OT hours, labor % of revenue), a
// stacked reg/OT pay bar chart by month, a department breakdown, and an
// "OT leaders" employee table (server-sorted by otHours desc; otPct > 10
// highlighted amber). CSV export covers the employee table.
//
// Month labels from the recipe are 'yyyy-MM' strings — rendered as-is.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, RefreshCw, AlertTriangle, Download, FileSpreadsheet } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';
import { apiV2 as api } from '../../services/apiV2';
import type { QbdPayrollEmployeeRow } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtPct = (v: number) => `${v.toFixed(1)}%`;
const fmtHrs = (v: number) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(v);

const MONTH_OPTIONS = [3, 6, 12];

const PAY_LABELS: Record<string, string> = { regPay: 'Regular Pay', otPay: 'OT Pay' };

function exportEmployees(rows: QbdPayrollEmployeeRow[], exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<QbdPayrollEmployeeRow> = [
    ['Employee',        (r) => r.name],
    ['Dept',            (r) => r.dept],
    ['Type',            (r) => r.type],
    ['Hourly Rate',     (r) => r.hourlyRate ?? ''],
    ['Gross Pay',       (r) => r.grossPay],
    ['Reg Hours',       (r) => r.regHours],
    ['OT Hours',        (r) => r.otHours],
    ['OT %',            (r) => r.otPct],
    ['Avg Weekly Hours', (r) => r.avgWeeklyHours],
    ['Weeks Worked',    (r) => r.weeksWorked],
  ];
  exporter('payroll-ot-leaders', cols, rows);
}

export function PayrollTab() {
  const [months, setMonths] = useState(12);

  const summaryQ = useQuery({
    queryKey: ['qbd', 'payroll-summary', months],
    queryFn: () => api.getQbdPayrollSummary({ months }),
    staleTime: 60_000,
    retry: 1,
  });

  const employeesQ = useQuery({
    queryKey: ['qbd', 'payroll-employees', months],
    queryFn: () => api.getQbdPayrollEmployees({ months }),
    staleTime: 60_000,
    retry: 1,
  });

  const totals = summaryQ.data?.totals;
  const buckets = summaryQ.data?.buckets ?? [];
  const depts = summaryQ.data?.byDepartment ?? [];
  const employees = employeesQ.data?.rows ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Users size={20} /> Payroll
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Labor cost and overtime from QuickBooks Desktop payroll.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            {MONTH_OPTIONS.map((m) => (
              <button key={m} onClick={() => setMonths(m)}
                className={`px-2 py-1.5 text-[11px] rounded-md border transition-all ${
                  months === m
                    ? 'bg-indigo-500/15 text-indigo-200 border-indigo-500/40'
                    : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'
                }`}>
                {m} months
              </button>
            ))}
          </div>
          <button onClick={() => { summaryQ.refetch(); employeesQ.refetch(); }} disabled={summaryQ.isLoading}
            className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
            <RefreshCw size={14} className={summaryQ.isFetching || employeesQ.isFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {summaryQ.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the payroll summary</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(summaryQ.error as Error).message}</div>
          </div>
        </div>
      )}

      {summaryQ.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !summaryQ.error && (
        <>
          {/* KPI tiles */}
          {totals && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Gross Pay</p>
                <p className="text-lg font-bold text-white">{fmtCur(totals.grossPay)}</p>
                <p className="text-[11px] text-slate-500">avg {fmtCur(totals.avgMonthlyGross)}/mo</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">OT Pay</p>
                <p className="text-lg font-bold text-white">{fmtCur(totals.otPay)}</p>
                <p className="text-[11px] text-slate-500">{fmtPct(totals.otPctOfPay)} of pay</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">OT Hours</p>
                <p className="text-lg font-bold text-white">{fmtHrs(totals.otHours)}</p>
                <p className="text-[11px] text-slate-500">{fmtPct(totals.otPctOfHours)} of hours</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Labor % of Revenue</p>
                <p className="text-lg font-bold text-white">{fmtPct(totals.laborPctRevenue)}</p>
              </div>
            </div>
          )}

          {/* Regular vs OT pay by month (stacked) */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Gross pay by month — regular vs overtime</h3>
            {buckets.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">No payroll activity in the selected period</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={buckets} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                    <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      interval="preserveStartEnd" minTickGap={24} />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                      tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(0)}k`} />
                    <Tooltip
                      cursor={{ fill: '#475569', fillOpacity: 0.15 }}
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                      formatter={(value, name) => [fmtCur(Number(value)), PAY_LABELS[String(name)] ?? String(name)]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }}
                      formatter={(v) => PAY_LABELS[String(v)] ?? String(v)} />
                    <Bar dataKey="regPay" stackId="pay" fill="#3b82f6" maxBarSize={32} />
                    <Bar dataKey="otPay" stackId="pay" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Department breakdown */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="px-4 pt-4 pb-2">
              <h3 className="text-sm font-semibold text-white">By department</h3>
            </div>
            {depts.length === 0 ? (
              <div className="text-center py-6 text-slate-500 text-sm">No department data in the selected period</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-700/50 bg-slate-800/50">
                      <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Department</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Headcount</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Gross Pay</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">OT Hours</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">OT Pay</th>
                      <th className="px-3 py-2.5 text-right text-slate-400 font-medium">% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {depts.map((d) => (
                      <tr key={d.dept} className="border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                        <td className="px-3 py-2 text-slate-200 whitespace-nowrap">{d.dept}</td>
                        <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtNum(d.headcount)}</td>
                        <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(d.grossPay)}</td>
                        <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtHrs(d.otHours)}</td>
                        <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur(d.otPay)}</td>
                        <td className="px-3 py-2 text-right text-slate-400 tabular-nums">{fmtPct(d.pctOfTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* OT leaders (qbd-payroll-employees, same months param) */}
      <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
        <div className="px-4 pt-4 pb-2 flex items-baseline justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-white">OT leaders</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Employees ranked by overtime hours · OT% over 10 highlighted</p>
          </div>
          <button
            onClick={() => exportEmployees(employees)}
            disabled={employees.length === 0}
            title="Export the employee table to CSV"
            className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
            <Download size={11} /> Export
          </button>
          <button
            onClick={() => exportEmployees(employees, exportXlsx)}
            disabled={employees.length === 0}
            title="Export to Excel"
            className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
            <FileSpreadsheet size={11} /> Excel
          </button>
        </div>
        {employeesQ.error ? (
          <div className="m-4 p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
            <AlertTriangle size={14} />
            <span className="text-xs">Couldn't load the employee breakdown: {(employeesQ.error as Error).message}</span>
          </div>
        ) : employeesQ.isLoading ? (
          <div className="flex items-center justify-center h-24"><RefreshCw className="w-5 h-5 text-blue-400 animate-spin" /></div>
        ) : employees.length === 0 ? (
          <div className="text-center py-6 text-slate-500 text-sm">No payroll activity in the selected period</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">Employee</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Dept</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Type</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Rate</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Gross Pay</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Reg Hrs</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">OT Hrs</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">OT %</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Avg Wk Hrs</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((r) => (
                  <tr key={r.empId} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                    <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-slate-200 whitespace-nowrap">{r.name}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.dept}</td>
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap capitalize">{r.type}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{r.hourlyRate != null ? fmtCur(r.hourlyRate) : '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.grossPay)}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtHrs(r.regHours)}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtHrs(r.otHours)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${r.otPct > 10 ? 'text-amber-400 font-medium' : 'text-slate-300'}`}>{fmtPct(r.otPct)}</td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtHrs(r.avgWeeklyHours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {employeesQ.data && (
          <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
            {employeesQ.data.dateFrom} → {employeesQ.data.dateTo} · {employees.length} employee{employees.length === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </div>
  );
}
